import React, { 
    useState,
    useContext,
    useEffect,
} from "react"

import { 
    InfoCircle, 
} from "react-bootstrap-icons"

import {
    Spinner,
} from "react-bootstrap"

import {
    useNavigate,
} from "react-router-dom"

import EMETLSegmentedControl from "../EMETLSegmentedControl"
import EMETLTaskPDFModal from "../EMETLTaskPDFModal"

import EMETLAssessmentManager, { 
    EMETLAssessmentTaskWithDetails, 
    EMETLAssessmentTaskRating,
} from "../../../Model/EMETLAssessment.m"

import { 
    NeedsEditor, 
} from "../EMETLTaskCellComponents"


import {
    Context, 
    EMETLContext,
} from "../../../Model/Contexts/AppContext"

import "./style.css"
import EMETLNavButton from "../EMETLNavButton"

type EMETLAssessmentTaskProps = {
    assessmentTask: EMETLAssessmentTaskWithDetails,
    autoSave?: (ID: number, rating: number, needs: string) => Promise<boolean>,
    saveButtonHandler?: (ID: number, rating: number, needs: string) => Promise<boolean>,
    navigateToPerformanceSteps?: () => void,
}

const EMETLAssessmentTask = (props?: EMETLAssessmentTaskProps) => {
    const navigate = useNavigate()
    const viewPerformanceSteps = async () => {
        navigate("/AssessmentTaskPerformanceSteps", {
            state: {
                atID: assessmentTask?.ID,
                taskNumber: assessmentTask?.Number,
            }
        })
    }

    const viewSubtasks = () => {
        navigate("/AssessmentTaskSubtasks", {
            state: {
                assessmentTask,
            }
        })
    }

    const { 
        assessmentTask, 
        autoSave,
        saveButtonHandler,
        navigateToPerformanceSteps = viewPerformanceSteps,
    } = props

    const {
        modal: {
            setContent,
            setTitle,
        }
    } = useContext(Context) as EMETLContext


    const [currentNeeds, setNeeds] = useState(assessmentTask?.SpecificNeeds ?? "")
    const [selectedRating, setSelectedRating] = useState(
        assessmentTask?.Rating ?? EMETLAssessmentManager.getDefaultTaskRating()
    )
    const [loading, setLoading] = useState(false)
    const [requiresSave, setRequiresSave] = useState(false)

    const showTaskPDF = () => {
        setContent(
            <EMETLTaskPDFModal
                task={assessmentTask}
            />
        )
        setTitle(assessmentTask.Number)
    }

    const saveAssessmentTask = async () => {
        let needs = currentNeeds
        if (selectedRating !== EMETLAssessmentTaskRating.TRAINED && !needs?.length) {
            alert(
                `You must enter specific needs unless the task is one of "${
                    EMETLAssessmentManager.getTaskRatingsWithoutNeeds().join(", ")
                }".`
            )

            return false
        } else if (EMETLAssessmentManager.getTaskRatingsWithoutNeeds().includes(selectedRating))
            needs = ""

        setLoading(true)
        const resp = await saveButtonHandler(assessmentTask?.ID, selectedRating, needs)
       
        if (!resp)
            alert("Error saving assessment task. Please try again.")
        else
            setRequiresSave(false)

        setLoading(false)
        
        return true
    }

    const onControlChange = (
        rating: number = null, 
        needs: string = "",
    ) => {
        if (needs)
            setNeeds(needs)

        if (rating !== null)
            setSelectedRating(rating)

        if (autoSave && typeof autoSave === "function")
            autoSave(assessmentTask?.TaskID ,rating ?? selectedRating, needs ?? currentNeeds)
    }

    useEffect(() => {
        const shouldSave = !autoSave
        if (currentNeeds !== assessmentTask?.SpecificNeeds || selectedRating !== assessmentTask?.Rating)
            setRequiresSave(shouldSave)
        else
            setRequiresSave(false)
    }, [currentNeeds, selectedRating])

    return (
        <div className="border-bottom pb-3">
            <div className="details-row">
                
                <span
                    style={{
                        flexGrow: 1,
                    }}
                >
                    <span 
                        className="bold"
                        style={{
                            fontSize: "20px",                                
                        }}
                    >
                        {assessmentTask.Number}
                    </span>
                    <a 
                        className="link-primary" 
                        onClick={showTaskPDF} 
                        role="button"
                    >
                        <InfoCircle />
                    </a>
                    <br />
                    {assessmentTask.Name}
                    <br />
                    {EMETLAssessmentManager.taskHasPerformanceSteps(assessmentTask) ? (
                        <a
                            onClick={navigateToPerformanceSteps}
                            className="text-secondary"
                            style={{
                                cursor: "pointer",
                            }}
                        >
                            Performance Steps
                        </a>
                    ) : (
                        <a
                            onClick={viewSubtasks}
                            className="text-secondary"
                            style={{
                                cursor: "pointer",
                            }}
                        >
                            Subtasks
                        </a>
                    )}
                </span>
                <div>
                    <EMETLSegmentedControl 
                        wrapperClass="tpu-wrapper" 
                        options={EMETLAssessmentManager.TASK_RATINGS} 
                        perform={onControlChange} 
                        selectedTab={selectedRating}
                    />
                </div>
            </div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    gap: "10px",
                }}
            >
                {
                    EMETLAssessmentManager.getTaskRatingsWhichRequireNeeds().includes(selectedRating) && (
                        loading ? (
                            <Spinner />
                        ) : (
                            <NeedsEditor 
                                className="needsEditor"
                                isTaskNeed={true} 
                                currentNeeds={currentNeeds} 
                                setNeeds={(n: string) => {
                                    onControlChange(null, n)
                                }} 
                            />
                        )
                    )
                }
                {
                    requiresSave && (
                        <EMETLNavButton
                            onClick={saveAssessmentTask}
                            style={{
                                alignSelf: "flex-end",
                            }}
                            disabled={loading}
                        >
                            {`${loading ? "Saving task" : "Save" } ${assessmentTask.Number}`}
                        </EMETLNavButton>
                    )
                }
            </div>
        </div>
    )
}

export default EMETLAssessmentTask